import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiMail, FiPhone, FiStar } from 'react-icons/fi';
import GripWallpaper from '@/assets/grip-wallpaper.jpg';
import { Button } from '@/components/common/Button';
import api from '@/utils/api';

interface TeamMember {
  _id: string;
  name: string;
  email?: string;
  phone?: string;
  department: string;
  position?: string;
  teamName?: string;
  avatar?: string;
  isTeamLead: boolean;
}

export const TeamsPage = () => {
  const navigate = useNavigate();
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [flippedCards, setFlippedCards] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      setLoading(true);
      const response = await api.get('/team-members');
      setMembers(response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch team members:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleFlip = (memberId: string) => {
    setFlippedCards(prev => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <Container>
        <LoadingText>Loading team members...</LoadingText>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <BackButton onClick={() => navigate(-1)} variant="secondary">
          <FiArrowLeft size={20} />
          Back
        </BackButton>
        <Title>Our Team</Title>
        <div style={{ width: '100px' }} /> {/* Spacer for centering */}
      </Header>

      <Content>
        {members.length === 0 ? (
          <EmptyState>
            <EmptyText>No team members yet</EmptyText>
          </EmptyState>
        ) : (
          <Grid>
            {members.map((member) => (
              <CardContainer
                key={member._id}
                onClick={() => toggleFlip(member._id)}
              >
                <FlipCard flipped={flippedCards.has(member._id)}>
                  {/* Front - Image with Name */}
                  <CardFront>
                    {member.avatar ? (
                      <MemberImage src={member.avatar} alt={member.name} />
                    ) : (
                      <PlaceholderImage>
                        {member.name.charAt(0).toUpperCase()}
                      </PlaceholderImage>
                    )}
                    <NameOverlay>
                      {member.isTeamLead && (
                        <LeaderIcon>
                          <FiStar size={16} />
                        </LeaderIcon>
                      )}
                      <NameBlock>
                        <MemberName>{member.name}</MemberName>
                        {member.position && (
                          <DetailDepartment style={{ color: '#fff', backgroundColor: 'orange' }}>{member.position}</DetailDepartment>
                        )}
                      </NameBlock>

                      <PositionBadge>
                        Click to view contact details
                      </PositionBadge>
                    </NameOverlay>
                  </CardFront>

                  {/* Back - Details */}
                  <CardBack>
                    <BackContent>
                      <DetailName>{member.name}</DetailName>
                      {member.position && (
                        <DetailPosition>{member.position}</DetailPosition>
                      )}
                      {member.department && (
                        <DetailDepartment>{member.department}</DetailDepartment>
                      )}
                      {member.teamName && (
                        <DetailTeam>{member.teamName}</DetailTeam>
                      )}
                      
                      <ContactSection>
                        {member.email && (
                          <a href={`mailto:${member.email}`} onClick={(e) => e.stopPropagation()}>
                            <ContactItem>
                              <FiMail size={16} />
                              <ContactText>{member.email}</ContactText>
                            </ContactItem>
                          </a>
                        )}
                        {member.phone && (
                          <a href={`tel:${member.phone}`} onClick={(e) => e.stopPropagation()}>
                            <ContactItem>
                              <FiPhone size={16} />
                              <ContactText>{member.phone}</ContactText>
                            </ContactItem>
                          </a>
                        )}
                      </ContactSection>

                      {member.isTeamLead && (
                        <LeaderBadge>
                          <FiStar size={14} />
                          Team Leader
                        </LeaderBadge>
                      )}
                    </BackContent>
                  </CardBack>
                </FlipCard>
              </CardContainer>
            ))}
          </Grid>
        )}
      </Content>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  padding: ${({ theme }) => theme.spacing.xl};
  overflow: hidden;
  background-image: url(${GripWallpaper});
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
  background-attachment: fixed;
  position: relative;
  
  & > * {
    position: relative;
    z-index: 1;
  }

  @media (max-width: 1024px) {
    padding: ${({ theme }) => `${theme.spacing.lg} ${theme.spacing.md}`};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => `${theme.spacing.md} ${theme.spacing.sm}`};
    overflow: visible;
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: clamp(0.75rem, 2vw, 1.5rem);
  flex-shrink: 0;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: 16px;
  padding: ${({ theme }) => theme.spacing.lg};
  box-shadow: ${({ theme }) => theme.shadows.lg};

  @media (max-width: 768px) {
    flex-wrap: wrap;
    gap: ${({ theme }) => theme.spacing.sm};
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 480px) {
    padding: ${({ theme }) => theme.spacing.sm};
  }
`;

const BackButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding: ${({ theme }) => `${theme.spacing.sm} ${theme.spacing.md}`};

  @media (max-width: 480px) {
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    font-size: ${({ theme }) => theme.fontSizes.sm};

    svg {
      width: 16px;
      height: 16px;
    }
  }
`;

const Title = styled.h1`
  font-size: clamp(1.5rem, 4vw, 2.5rem);
  font-weight: 800;
  color: #1a202c;
  margin: 0;
  text-align: center;
`;

const Content = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.lg};
  max-width: 1200px;
  width: 100%;
  margin: 0 auto;
  
  &::-webkit-scrollbar {
    width: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 10px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.5);
    }
  }

  @media (max-width: 1024px) {
    padding: ${({ theme }) => theme.spacing.md};
  }

  @media (max-width: 768px) {
    padding: ${({ theme }) => theme.spacing.md} 0;
    overflow: visible;
  }

  @media (max-width: 640px) {
    padding: 0;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: clamp(1rem, 2vw, 2rem);
  padding-bottom: 2rem;

  @media (max-width: 640px) {
    grid-template-columns: minmax(0, 1fr);
    gap: ${({ theme }) => theme.spacing.md};
  }
`;

const CardContainer = styled.div`
  perspective: 1000px;
  cursor: pointer;
  height: 400px;
  position: relative;

  @media (max-width: 640px) {
    height: 360px;
  }
`;

const FlipCard = styled.div<{ flipped: boolean }>`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.6s;
  transform-style: preserve-3d;
  transform: ${props => props.flipped ? 'rotateY(180deg)' : 'rotateY(0)'};
  will-change: transform;
`;

const CardFace = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  backface-visibility: hidden;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04);
`;

const CardFront = styled(CardFace)`
  position: relative;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
`;

const MemberImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const PlaceholderImage = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  font-size: 6rem;
  font-weight: 700;
  color: white;
`;

const NameOverlay = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 2rem 1.5rem 1.5rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  text-align: center;
`;

const NameBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
`;

const MemberName = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: white;
  margin: 0;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
`;

const PositionBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  background: rgba(255, 255, 255, 0.15);
  color: #e2e8f0;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
`;

const LeaderIcon = styled.div`
  color: #fbbf24;
  filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
  margin-bottom: 0.5rem;
`;

const CardBack = styled(CardFace)`
  transform: rotateY(180deg);
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow-y: auto;
  top: 0;
`;

const BackContent = styled.div`
  padding: 2rem;
  text-align: center;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 10px;
  }
`;

const DetailName = styled.h3`
  font-size: 1.75rem;
  font-weight: 700;
  color: #1a202c;
  margin: 0 0 0.5rem 0;
`;

const DetailPosition = styled.div`
  font-size: 1.1rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.primary};
  margin-bottom: 1rem;
`;

const DetailDepartment = styled.div`
  display: inline-block;
  padding: 0.5rem 1rem;
  background: ${({ theme }) => theme.colors.primary}20;
  color: ${({ theme }) => theme.colors.primary};
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const DetailTeam = styled.div`
  font-size: 0.9rem;
  color: #718096;
  font-style: italic;
  margin-bottom: 1.5rem;
`;

const ContactSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.1);
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  color: #718096;
  font-size: 0.95rem;
`;

const ContactText = styled.span`
  word-break: break-all;
`;

const LeaderBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1.5rem;
  padding: 0.5rem 1rem;
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
  border-radius: 20px;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 2px 8px rgba(251, 191, 36, 0.3);
`;

const LoadingText = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1a202c;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  border-radius: 16px;
  padding: 3rem;
`;

const EmptyText = styled.p`
  font-size: 1.5rem;
  font-weight: 600;
  color: #718096;
`;
